import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  Divider,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Computer as ComputerIcon,
  Smartphone as SmartphoneIcon,
  Tablet as TabletIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { blue, green, red, grey, orange, purple } from "@mui/material/colors";
import FormatDateDurationForMessagesDate from "../../utils/FormatDateDurationForMessagesDate";
import capitalizeEachFirstWord from "../../utils/CapLetters";

const SessionDetailModal = ({ open, session, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "Invalid date";
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return FormatDateDurationForMessagesDate(dateString, true);
    } catch {
      return "Invalid date";
    }
  };

  const getUserTypeIcon = (userType) => {
    return userType === "admin" ? (
      <AdminPanelSettingsIcon fontSize="small" />
    ) : (
      <PersonIcon fontSize="small" />
    );
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return <ComputerIcon fontSize="small" />;

    const ua = userAgent.toLowerCase();
    if (
      ua.includes("mobile") ||
      ua.includes("android") ||
      ua.includes("iphone")
    ) {
      return <SmartphoneIcon fontSize="small" />;
    }
    if (ua.includes("tablet") || ua.includes("ipad")) {
      return <TabletIcon fontSize="small" />;
    }
    return <ComputerIcon fontSize="small" />;
  };

  const parseUserAgent = (userAgent) => {
    if (!userAgent)
      return { browser: "Unknown", os: "Unknown", full: "Unknown Device" };

    let browser = "Unknown";
    let os = "Unknown";

    // Browser detection
    if (userAgent.includes("Chrome") && !userAgent.includes("Chromium"))
      browser = "Chrome";
    else if (userAgent.includes("Firefox")) browser = "Firefox";
    else if (userAgent.includes("Safari") && !userAgent.includes("Chrome"))
      browser = "Safari";
    else if (userAgent.includes("Edge")) browser = "Edge";
    else if (userAgent.includes("Opera")) browser = "Opera";

    // OS detection
    if (userAgent.includes("Windows NT 10.0")) os = "Windows 10/11";
    else if (userAgent.includes("Windows NT 6.3")) os = "Windows 8.1";
    else if (userAgent.includes("Windows NT 6.1")) os = "Windows 7";
    else if (userAgent.includes("Windows")) os = "Windows";
    else if (userAgent.includes("Mac OS X")) {
      const macMatch = userAgent.match(/Mac OS X (\d+[._]\d+)/);
      os = macMatch ? `macOS ${macMatch[1].replace(/_/g, ".")}` : "macOS";
    } else if (userAgent.includes("Linux")) os = "Linux";
    else if (userAgent.includes("Android")) {
      const androidMatch = userAgent.match(/Android (\d+\.?\d*)/);
      os = androidMatch ? `Android ${androidMatch[1]}` : "Android";
    } else if (userAgent.includes("iPhone OS")) {
      const iosMatch = userAgent.match(/iPhone OS (\d+_\d+)/);
      os = iosMatch ? `iOS ${iosMatch[1].replace(/_/g, ".")}` : "iOS";
    }

    return {
      browser,
      os,
      full: `${browser} on ${os}`,
    };
  };

  const getSessionDuration = (createdAt) => {
    if (!createdAt) return "Unknown";
    try {
      const start = new Date(createdAt);
      const now = new Date();
      const diffMs = now - start;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const hours = Math.floor(diffMins / 60);
      const minutes = diffMins % 60;

      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    } catch {
      return "Unknown";
    }
  };

  if (!session) return null;

  const deviceInfo = parseUserAgent(session.userAgent);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, px: 3, pt: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Avatar
              sx={{
                bgcolor: session.userType === "admin" ? blue[500] : green[500],
                width: 52,
                height: 52,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                border: "3px solid white",
              }}
            >
              {getUserTypeIcon(session.userType)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  background: "linear-gradient(45deg, #1976d2, #42a5f5)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {session.firstName && session.lastName
                  ? `${capitalizeEachFirstWord(
                      session.firstName
                    )} ${capitalizeEachFirstWord(session.lastName)}`
                  : capitalizeEachFirstWord(session.username) || "Unknown User"}
              </Typography>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: grey[100],
                  borderRadius: 2,
                  border: `1px solid ${grey[200]}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    backgroundColor: session.isActive ? green[500] : red[500],
                    borderRadius: "50%",
                    animation: session.isActive ? "pulse 2s infinite" : "none",
                    "@keyframes pulse": {
                      "0%": { opacity: 1 },
                      "50%": { opacity: 0.5 },
                      "100%": { opacity: 1 },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: grey[600],
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    fontFamily: "monospace",
                    letterSpacing: 0.5,
                  }}
                >
                  Session ID: {session.sessionId?.slice(-12)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Tooltip title="Close">
            <IconButton
              onClick={onClose}
              sx={{
                bgcolor: grey[50],
                border: `1px solid ${grey[200]}`,
                "&:hover": {
                  bgcolor: grey[100],
                  transform: "scale(1.05)",
                  transition: "all 0.2s ease",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Grid container spacing={3}>
          {/* User Information Section */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2.5,
                height: "100%",
                borderRadius: 3,
                border: `1px solid ${grey[200]}`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: "1.1rem",
                  color: grey[800],
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 20,
                    backgroundColor: blue[500],
                    borderRadius: 2,
                  }}
                />
                User Information
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    Email
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                      color: "text.primary",
                      lineHeight: 1.3,
                    }}
                  >
                    {session.email}
                  </Typography>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    User Type
                  </Typography>
                  <Chip
                    icon={getUserTypeIcon(session.userType)}
                    label={
                      session.userType === "admin" ? "Administrator" : "User"
                    }
                    size="small"
                    color={
                      session.userType === "admin" ? "secondary" : "primary"
                    }
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.75rem",
                      height: 24,
                      "& .MuiChip-icon": { fontSize: "0.9rem" },
                    }}
                  />
                </Box>

                {session.role && session.userType === "admin" && (
                  <>
                    <Divider sx={{ my: 0.5 }} />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                      >
                        Admin Role
                      </Typography>
                      <Chip
                        label={session.role.replace("_", " ").toUpperCase()}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{
                          fontWeight: 400,
                          fontSize: "0.75rem",
                          height: 24,
                        }}
                      />
                    </Box>
                  </>
                )}

                <Divider sx={{ my: 0.5 }} />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    User ID
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                      color: "text.primary",
                      lineHeight: 1.3,
                      fontFamily: "monospace",
                    }}
                  >
                    {session.userId}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Session Details Section */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2.5,
                height: "100%",
                borderRadius: 3,
                border: `1px solid ${grey[200]}`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: "1.1rem",
                  color: grey[800],
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 20,
                    backgroundColor: green[500],
                    borderRadius: 2,
                  }}
                />
                Session Details
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    Session Started
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                      color: "text.primary",
                      lineHeight: 1.3,
                    }}
                  >
                    {formatDate(session.createdAt)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    Last Activity
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.8rem",
                      color: "text.primary",
                      lineHeight: 1.3,
                    }}
                  >
                    {formatRelativeTime(session.lastActivity)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    Session Duration
                  </Typography>
                  <Chip
                    icon={<ScheduleIcon fontSize="small" />}
                    label={getSessionDuration(session.createdAt)}
                    size="small"
                    color="info"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.75rem",
                      height: 24,
                      "& .MuiChip-icon": { fontSize: "0.9rem" },
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Device Information Section */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${grey[200]}`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: "1.1rem",
                  color: grey[800],
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 20,
                    backgroundColor: purple[500],
                    borderRadius: 2,
                  }}
                />
                Device Information
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    backgroundColor: "rgba(255,255,255,0.7)",
                    borderRadius: 2.5,
                    border: `1px solid ${grey[100]}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: orange[500],
                      width: 34,
                      height: 34,
                      mr: 2,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    }}
                  >
                    {getDeviceIcon(session.userAgent)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.7rem",
                        mb: 0.3,
                        fontWeight: 600,
                      }}
                    >
                      Browser & OS
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        color: grey[800],
                      }}
                    >
                      {deviceInfo.full}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.8, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    User Agent
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 400,
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      lineHeight: 1.4,
                      wordBreak: "break-all",
                      bgcolor: grey[50],
                      p: 1,
                      borderRadius: 1,
                      fontFamily: "monospace",
                    }}
                  >
                    {session.userAgent || "Not available"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Network Information Section */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${grey[200]}`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 2.5,
                  fontSize: "1.1rem",
                  color: grey[800],
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 20,
                    backgroundColor: red[500],
                    borderRadius: 2,
                  }}
                />
                Network Information
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    backgroundColor: "rgba(255,255,255,0.7)",
                    borderRadius: 2.5,
                    border: `1px solid ${grey[100]}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: red[500],
                      width: 34,
                      height: 34,
                      mr: 2,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    }}
                  >
                    <LocationOnIcon sx={{ fontSize: "1rem" }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.7rem",
                        mb: 0.3,
                        fontWeight: 600,
                      }}
                    >
                      IP Address
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        color: grey[800],
                        fontFamily: "monospace",
                      }}
                    >
                      {session.ipAddress}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    backgroundColor: "rgba(255,255,255,0.7)",
                    borderRadius: 2.5,
                    border: `1px solid ${grey[100]}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: green[600],
                      width: 34,
                      height: 34,
                      mr: 2,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    }}
                  >
                    <SecurityIcon sx={{ fontSize: "1rem" }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.7rem",
                        mb: 0.3,
                        fontWeight: 600,
                      }}
                    >
                      Status
                    </Typography>
                    <Chip
                      label={session.isActive ? "Active" : "Inactive"}
                      size="small"
                      color={session.isActive ? "success" : "default"}
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        height: 24,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            minWidth: 120,
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            background: "linear-gradient(45deg, #1976d2, #42a5f5)",
            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
            "&:hover": {
              background: "linear-gradient(45deg, #1565c0, #1976d2)",
              transform: "translateY(-2px)",
              boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionDetailModal;
